import {
  type PriceBucket,
  applyDiscountToBuckets,
  topUpsFirst,
} from "@/lib/discount-price-buckets";

import {
  Discount,
  DiscountDetail,
  DiscountUse,
} from "@/types/billing/discount";
import { DiscountType } from "@/types/enums/discount";
import { ItemType } from "@/types/enums/item-type";

/**
 * Utility functions for calculating discounts across different item types.
 */

/** Human-readable label for what a benefit applies to. */
export function getDiscountName(discount: DiscountDetail): string {
  if (discount.item) return discount.item.name;
  if (discount.category) return `${discount.category} Category`;
  if (discount.itemType) {
    switch (discount.itemType) {
      case ItemType.PRODUCT:
        return "Products";
      case ItemType.SERVICE:
        return "Services";
      case ItemType.PACKAGE:
        return "Packages";
      case ItemType.MEMBERSHIP:
        return "Memberships";
      case ItemType.MEDICATION:
        return "Medications";
      case ItemType.SERVICE_FEE:
        return "Service Fees";
      case ItemType.ITEM:
        return "All Items";
      default:
        return "Unknown Type";
    }
  }
  return "Benefit";
}

/**
 * Calculate discount per unit for display purposes.
 * Used by quote-view.tsx for sent quote line items.
 */
export function calculateDiscountPerUnit(
  unitPrice: number,
  discountAmount: number = 0,
  discountPercentage: number = 0,
  applyBothDiscounts: boolean = true,
): number {
  if (!applyBothDiscounts) {
    // Either fixed amount or percentage, not both
    if (discountAmount > 0) {
      return discountAmount;
    } else if (discountPercentage > 0) {
      return (unitPrice * discountPercentage) / 100;
    }
    return 0;
  }

  // Both discounts can apply
  let discountPerUnit = discountAmount;
  if (discountPercentage > 0) {
    discountPerUnit += (unitPrice * discountPercentage) / 100;
  }

  return discountPerUnit;
}

/**
 * Calculate the discount amount for a given discount, item price, and quantity.
 * For fixed amount discounts, multiplies by quantity (amount is per-item).
 * For percentage discounts, calculates percentage of total (itemPrice * quantity).
 *
 * @param discount - The discount values off any discount-shaped model: a
 *                   DiscountDetail, CreateCustomDiscountRequest, or DiscountTemplate
 * @param itemPrice - Price per unit in cents
 * @param quantity - Number of units
 * @returns Total discount amount in cents
 */
export function calculateDiscountAmount(
  discount: Pick<Discount, "amount" | "percentage"> & {
    additionalCharge?: number | null;
  },
  itemPrice: number,
  quantity: number,
): number {
  // A prepaid use with an extra charge covers everything but the extra, so a
  // pricier service spends the same credit and the member pays the rest.
  if (discount.additionalCharge && discount.percentage === 100) {
    return Math.max(0, itemPrice - discount.additionalCharge) * quantity;
  }
  // Prefer amount over percentage when both exist - amount is more accurate
  if (discount.amount && discount.amount > 0) {
    // Fixed amount discount - multiply by quantity (amount is per-item)
    // Cap to not exceed the item total
    return Math.min(discount.amount * quantity, itemPrice * quantity);
  }
  if (discount.percentage && discount.percentage > 0) {
    // Percentage-based discount - calculate percentage of total
    return Math.round((itemPrice * quantity * discount.percentage) / 100);
  }
  return 0;
}

/**
 * The promotion a discount use belongs to, whether it is still unsaved
 * (`createDiscount`) or already persisted and hydrated back from the API
 * (`discount`). Reading only the unsaved side makes a reloaded promotion
 * invisible, which lets the auto-apply pass add a second allocation.
 *
 * Gated on the discount type, not on `promotionId` alone: the analytics
 * backfill tags some GLOBAL/CUSTOM discounts with a promotion_id without
 * changing their type (see `Discount.source_label` in the backend), and those
 * are manual discounts a human applied — not promotion uses. Matching them
 * here would let the applier skip, debit, or strip someone's manual discount.
 */
export function discountUsePromotionId(use: DiscountUse): string | undefined {
  const side = use.createDiscount ?? use.discount;
  if (!side || side.type !== DiscountType.PROMOTION) return undefined;
  return side.promotionId;
}

/** The percentage a discount use carries, from either side of the same split. */
export function discountUsePercentage(use: DiscountUse): number | undefined {
  return use.discount?.percentage ?? use.createDiscount?.percentage;
}

/**
 * Discount use data structure for sequential calculation
 */
export interface DiscountUseData {
  amount?: number; // Fixed discount amount in cents (per item)
  percentage?: number; // Percentage discount (0-100)
  additionalCharge?: number; // Cents the member still pays per unit of a prepaid use
  quantity: number; // Number of units this discount applies to
}

/**
 * Calculate total discount by applying discounts sequentially in order.
 * Percentages are calculated on the running total (compounding).
 *
 * @param originalAmount - Original total amount in cents
 * @param discounts - Array of discount uses, sorted by order
 * @param itemQuantity - Total number of items (for calculating applicable units)
 * @returns Total discount amount in cents
 */
export function calculateSequentialDiscount(
  originalAmount: number,
  discounts: DiscountUseData[],
  itemQuantity: number = 1,
): number {
  if (!discounts || discounts.length === 0) {
    return 0;
  }

  if (itemQuantity <= 0) {
    return 0;
  }

  // Track like-priced units as buckets rather than an index-per-unit array —
  // Array(itemQuantity) throws RangeError for a fractional line quantity like
  // 1.5. Buckets carry the fractional count through the allocation without
  // that hazard, and merge back down after each pass to stay small.
  let buckets: PriceBucket[] = [
    { quantity: itemQuantity, remainingPrice: originalAmount / itemQuantity },
  ];

  for (const index of topUpsFirst(discounts)) {
    buckets = applyDiscountToBuckets(
      buckets,
      discounts[index],
      originalAmount / itemQuantity,
      itemQuantity,
    ).buckets;
  }

  const remainingAmount = Math.round(
    buckets.reduce(
      (sum, bucket) => sum + bucket.remainingPrice * bucket.quantity,
      0,
    ),
  );
  return originalAmount - remainingAmount;
}
